import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../services/api';
import type { CabinetTariff, OrdProvider } from '../types';

export type AuthUser = {
  userId: number;
  login: string;
  role: 'OWNER' | 'MANAGER' | 'STAFF' | 'CLIENT_ADMIN';
  inn?: string;
  personName?: string;
  allowSelfAd?: boolean;
  accessCreatives?: boolean;
  accessPersons?: boolean;
  accessContracts?: boolean;
  accessInvoices?: boolean;
  ordClientId?: number;
  ordClientName?: string;
  tariff?: CabinetTariff;
  ordProvider?: OrdProvider;
  subscriptionPaidUntil?: string;
  subscriptionActive?: boolean;
  testModeEnabled?: boolean;
  statsSubmissionEnabled?: boolean;
  dzenSubmissionEnabled?: boolean;
};

type AuthContextValue = {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  isOwner: boolean;
  isManager: boolean;
  isStaff: boolean;
  isClientAdmin: boolean;
  canCreatives: boolean;
  canPersons: boolean;
  canContracts: boolean;
  canInvoices: boolean;
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const TOKEN_KEY = 'cabinet_auth_token';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setUser(null);
      setToken(null);
      setLoading(false);
      return;
    }
    try {
      const me = await authApi.me();
      setUser(me);
      setToken(t);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (loginName: string, password: string) => {
    const res = await authApi.login(loginName, password);
    localStorage.setItem(TOKEN_KEY, res.token);
    setToken(res.token);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const isOwner = user?.role === 'OWNER';
  const isClientAdmin = user?.role === 'CLIENT_ADMIN';
  const isManager = user?.role === 'MANAGER';
  const isStaff = user?.role === 'STAFF';

  const canCreatives = !!(isOwner || isClientAdmin || user?.accessCreatives);
  const canPersons = !!(isOwner || isClientAdmin || user?.accessPersons);
  const canContracts = !!(isOwner || isClientAdmin || user?.accessContracts);
  const canInvoices = !!(isOwner || isClientAdmin || user?.accessInvoices);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      isOwner,
      isManager,
      isStaff,
      isClientAdmin,
      canCreatives,
      canPersons,
      canContracts,
      canInvoices,
      login,
      logout,
      refreshMe,
    }),
    [user, token, loading, isOwner, isManager, isStaff, isClientAdmin, canCreatives, canPersons, canContracts, canInvoices, login, logout, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth outside AuthProvider');
  }
  return ctx;
}
